/**
 * PortfolioImpactPanel — Sprint 20 reusable P13 component.
 *
 * Shows how the current page's data connects to the user's Core/Active watchlist.
 * Used across retrofitted pages (/macro-radar, /currency, /credibility, /telegram,
 * /voices, /signals, /smart-money, /mnemosyne).
 *
 * Props:
 *   topic (required) — string label shown in header (e.g. "Regime", "Currency")
 *   affected_tickers — array of { ticker, tier, name?, impact?, direction? }
 *   impact_summary — short prose
 *   loading — optional boolean
 *   onTickerClick — optional (ticker) => void
 *
 * Always renders (never null). Shows explicit loading / empty state so users
 * know the page has been checked.
 */

import { Target, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const DIRECTION_ICON = {
  positive: { Icon: TrendingUp, color: '#10b981' },
  negative: { Icon: TrendingDown, color: '#ef4444' },
  neutral:  { Icon: Minus,      color: '#6b7280' },
};

const TIER_COLORS = {
  core:    { color: '#10b981', label: 'CORE' },
  active:  { color: '#f59e0b', label: 'ACTIVE' },
  monitor: { color: '#6b7280', label: 'MON' },
};

export default function PortfolioImpactPanel({
  topic,
  affected_tickers = [],
  impact_summary,
  loading = false,
  onTickerClick,
}) {
  const core = affected_tickers.filter(t => t.tier === 'core');
  const active = affected_tickers.filter(t => t.tier === 'active');
  const other = affected_tickers.filter(t => t.tier !== 'core' && t.tier !== 'active');

  return (
    <div className="bg-korinth-surface border-l-4 border-l-korinth-accent border border-korinth-border rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-4 h-4 text-korinth-accent" />
        <span className="text-xs font-mono font-bold uppercase tracking-wider text-korinth-accent">
          Your Exposure · {topic}
        </span>
        {affected_tickers.length > 0 && (
          <span className="text-[10px] font-mono text-korinth-muted ml-auto">
            {core.length} core · {active.length} active{other.length > 0 ? ` · ${other.length} monitor` : ''}
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-[11px] font-mono text-korinth-muted py-1">
          <span className="animate-pulse">● </span>Loading your book...
        </div>
      ) : affected_tickers.length === 0 ? (
        <div className="text-[11px] font-mono text-korinth-muted leading-relaxed">
          No direct exposure in your Core/Active watchlist for this topic.
          {impact_summary ? ` ${impact_summary}` : ''}
          <div className="text-[10px] text-korinth-muted/70 mt-1">
            Add tickers to your <a href="/watchlist" className="text-korinth-accent hover:underline">watchlist</a> to see tailored impact here.
          </div>
        </div>
      ) : (
        <>
          {impact_summary && (
            <div className="text-[11px] font-mono text-korinth-text mb-2 leading-relaxed">
              {impact_summary}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {affected_tickers.slice(0, 16).map(t => {
              const tierStyle = TIER_COLORS[t.tier] || TIER_COLORS.monitor;
              const dir = DIRECTION_ICON[t.direction] || DIRECTION_ICON.neutral;
              const DirIcon = dir.Icon;
              const clickable = typeof onTickerClick === 'function';
              return (
                <button
                  key={t.ticker}
                  onClick={clickable ? () => onTickerClick(t.ticker) : undefined}
                  title={t.impact || t.name || t.ticker}
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded border border-korinth-border/40 bg-korinth-bg text-[10px] font-mono transition-colors ${clickable ? 'hover:border-korinth-accent/50 cursor-pointer' : 'cursor-default'}`}
                >
                  <span className="text-white font-bold">{t.ticker}</span>
                  <span className="text-[8px] font-bold uppercase" style={{ color: tierStyle.color }}>
                    {tierStyle.label}
                  </span>
                  <DirIcon className="w-2.5 h-2.5" style={{ color: dir.color }} />
                </button>
              );
            })}
            {affected_tickers.length > 16 && (
              <span className="text-[9px] font-mono text-korinth-muted self-center">+{affected_tickers.length - 16} more</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
